import * as React from 'react';

import { buildRelayCircuitMultiaddrs } from './multiaddr';
import type { SidecarConnection } from './types';
import { useLibp2p } from './useLibp2p';

import { useIdentity } from '~/shared/hooks';

const RETRY_BASE_DELAY = 2_000;
const RETRY_MAX_DELAY = 30_000;
const MAX_RECONNECT_RETRIES = 3;
const DIRECT_DIAL_TIMEOUT_MS = 4_000;
const RELAY_DIAL_TIMEOUT_MS = 8_000;
const UPGRADE_INTERVAL_MS = 15_000;
const UPGRADE_MAX_ATTEMPTS = 10;

export function useSidecarConnection(params: {
  sidecarId: string | null | undefined;
  peerId: string | null | undefined;
  directMultiaddrs: string[];
  relayMultiaddrs: string[];
  enabled: boolean;
}): {
  connection: SidecarConnection | undefined;
  isConnecting: boolean;
  connectionFailed: boolean;
} {
  const { directMultiaddrs, relayMultiaddrs, enabled } = params;
  const sidecarId = params.sidecarId ?? undefined;
  const peerId = params.peerId ?? undefined;
  const { accessToken } = useIdentity();
  const { nodeReady, connect, getConnection, upgradeConnection, connections } =
    useLibp2p();
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [connectionFailed, setConnectionFailed] = React.useState(false);
  const retryCountRef = React.useRef(0);
  const retryTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  const connection = sidecarId ? connections.get(sidecarId) : undefined;

  React.useEffect(() => {
    if (!nodeReady || !enabled || !sidecarId || !peerId) return;
    retryCountRef.current = 0;
    setConnectionFailed(false);

    const relayCircuitMultiaddrs =
      relayMultiaddrs.length > 0
        ? buildRelayCircuitMultiaddrs(relayMultiaddrs, peerId)
        : [];

    if (directMultiaddrs.length === 0 && relayCircuitMultiaddrs.length === 0) {
      return;
    }

    const existingConn = getConnection(sidecarId);
    if (existingConn?.connected) return;

    let cancelled = false;

    const attempt = async () => {
      if (cancelled) return;
      setIsConnecting(true);

      let directError: unknown = null;

      if (directMultiaddrs.length > 0) {
        try {
          await connect(sidecarId, peerId, directMultiaddrs, {
            dialTimeoutMs: DIRECT_DIAL_TIMEOUT_MS,
          });
          if (!cancelled) {
            retryCountRef.current = 0;
            setIsConnecting(false);
          }
          return;
        } catch (err) {
          directError = err;
        }
      }

      if (cancelled) return;

      if (relayCircuitMultiaddrs.length > 0) {
        try {
          await connect(sidecarId, peerId, relayCircuitMultiaddrs, {
            dialTimeoutMs: RELAY_DIAL_TIMEOUT_MS,
            authToken: accessToken,
          });
          if (!cancelled) {
            retryCountRef.current = 0;
            setIsConnecting(false);
          }
          return;
        } catch (relayErr) {
          if (!cancelled) {
            console.warn(
              'libp2p sidecar connect failed',
              { sidecarId, peerId },
              directError
                ? new AggregateError(
                    [directError, relayErr],
                    'failed to connect via direct and relay paths',
                  )
                : relayErr,
            );
          }
        }
      } else if (directError && !cancelled) {
        console.warn('libp2p sidecar connect failed (direct only)', {
          sidecarId,
          peerId,
          error: directError,
        });
      }

      if (cancelled) return;
      setIsConnecting(false);

      retryCountRef.current++;
      if (retryCountRef.current >= MAX_RECONNECT_RETRIES) {
        setConnectionFailed(true);
        return;
      }
      const delay = Math.min(
        RETRY_BASE_DELAY * Math.pow(2, retryCountRef.current - 1),
        RETRY_MAX_DELAY,
      );
      retryTimerRef.current = setTimeout(() => {
        if (!cancelled) attempt();
      }, delay);
    };

    attempt();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      setIsConnecting(false);
    };
  }, [
    nodeReady,
    enabled,
    sidecarId,
    peerId,
    directMultiaddrs,
    relayMultiaddrs,
    accessToken,
    connect,
    getConnection,
    connection?.connected,
  ]);

  React.useEffect(() => {
    if (!connection?.connected || connection.type !== 'relayed') return;
    if (!sidecarId || !peerId || directMultiaddrs.length === 0) return;

    let cancelled = false;
    let attemptCount = 0;

    const intervalId = setInterval(async () => {
      if (cancelled) return;
      attemptCount++;

      if (attemptCount > UPGRADE_MAX_ATTEMPTS) {
        clearInterval(intervalId);
        return;
      }

      const upgraded = await upgradeConnection(
        sidecarId,
        peerId,
        directMultiaddrs,
        {
          dialTimeoutMs: DIRECT_DIAL_TIMEOUT_MS,
        },
      );

      if (upgraded) {
        clearInterval(intervalId);
      }
    }, UPGRADE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    connection?.connected,
    connection?.type,
    sidecarId,
    peerId,
    directMultiaddrs,
    upgradeConnection,
  ]);

  return { connection, isConnecting, connectionFailed };
}
